import {
  getAccessToken,
  getRefreshToken,
  updateAccessToken,
  clearAuthTokens,
  isAccessTokenExpiringSoon,
  hasValidRefreshToken
} from "../storage/authStorage"
import i18n from "i18next"
import axios, { AxiosRequestConfig, Method } from "axios"

type RequestOptions = {
  url: string
  data?: unknown
  auth?: boolean
  headers?: Record<string, string>
  skipAuthHandling?: boolean
}

type FileRequestOptions = {
  url: string
  data: FormData
  auth?: boolean
}

type RequestConfigWithRetry = AxiosRequestConfig & {
  _retry?: boolean
}

type BlobRequestOptions = {
  url: string
  auth?: boolean
}

type BlobResponse = {
  blob: Blob
  filename: string
}

// Track ongoing refresh to prevent multiple simultaneous refresh requests
let refreshPromise: Promise<string | null> | null = null

/**
 * Refresh the access token using the refresh token
 */
const refreshAccessToken = async (): Promise<string | null> => {
  if (refreshPromise) {
    return refreshPromise
  }

  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return null
  }

  refreshPromise = (async () => {
    try {
      const response = await axios({
        method: "POST",
        url: process.env.REACT_APP_API_URL + "/api/v1/auth/refresh",
        data: { refreshToken },
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        }
      })

      const { accessToken, accessTokenExpiresAt } = response.data
      updateAccessToken({ accessToken, accessTokenExpiresAt })
      return accessToken
    } catch (error) {
      console.error("Token refresh failed:", error)
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Ensure we have a valid access token, refreshing if necessary
 */
const ensureValidToken = async (): Promise<string | undefined> => {
  if (isAccessTokenExpiringSoon() && hasValidRefreshToken()) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      return newToken
    }
  }

  return getAccessToken()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ResponseTransformer<T> = (response: any) => T

/**
 * Handle authentication errors with token refresh retry
 */
const handleAuthError = async <T>(
  error: unknown,
  originalRequest: RequestConfigWithRetry,
  transformResponse: ResponseTransformer<T> = response => response.data
): Promise<T> => {
  const { status, forceLogout } = (error as { response?: { status?: number; forceLogout?: boolean } }).response ?? {}

  // Handle 401 Unauthorized - try refresh fetch with new auth token
  if (!forceLogout && status === 401 && !originalRequest._retry && hasValidRefreshToken()) {
    originalRequest._retry = true
    const newToken = await refreshAccessToken()

    if (newToken) {
      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${newToken}`
      }
      const response = await axios(originalRequest)
      return transformResponse(response)
    }
  }

  // Logout on forceLogout or 401 (after failed refresh attempt)
  if (forceLogout || status === 401) {
    clearAuthTokens()
    window.location.href = "/login"
  }

  throw error
}

/**
 * Build request headers with optional authentication
 */
const getHeaders = async (
  authRequired?: boolean,
  additionalHeaders: Record<string, string> = {}
): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Accept-Language": i18n.resolvedLanguage || "en",
    ...additionalHeaders
  }

  if (authRequired) {
    const token = await ensureValidToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  return headers
}

/**
 * General request handler
 */
const requestHandler = async <T>(
  method: Method,
  { url, data, auth, headers: additionalHeaders = {}, skipAuthHandling = false }: RequestOptions
): Promise<T> => {
  const fullUrl = process.env.REACT_APP_API_URL + url
  const headers = await getHeaders(auth, additionalHeaders)

  const requestConfig: RequestConfigWithRetry = {
    method,
    url: fullUrl,
    data,
    headers
  }

  try {
    const response = await axios(requestConfig)
    return response.data
  } catch (error) {
    if (skipAuthHandling) {
      throw error
    }
    return handleAuthError<T>(error, requestConfig)
  }
}

/**
 * File upload request handler
 */
const fileRequestHandler = async <T>(method: Method, { url, data, auth }: FileRequestOptions): Promise<T> => {
  const headers = await getHeaders(auth, { "Content-Type": "multipart/form-data" })

  const requestConfig: RequestConfigWithRetry = {
    method,
    url: process.env.REACT_APP_API_URL + url,
    data,
    headers
  }

  try {
    const response = await axios(requestConfig)
    return response.data
  } catch (error) {
    return handleAuthError<T>(error, requestConfig)
  }
}

const extractBlobResponse = (response: { data: Blob; headers: Record<string, string> }): BlobResponse => {
  const blob = response.data

  let filename = "download"
  const contentDisposition = response.headers["content-disposition"]
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="([^"]+)"/)
    if (match?.[1]) {
      filename = match[1]
    }
  }

  return { blob, filename }
}

/**
 * Blob/binary request handler for file downloads
 */
const blobRequestHandler = async (method: Method, { url, auth }: BlobRequestOptions): Promise<BlobResponse> => {
  const headers = await getHeaders(auth)

  const requestConfig: RequestConfigWithRetry = {
    method,
    url: process.env.REACT_APP_API_URL + url,
    headers,
    responseType: "blob"
  }

  try {
    const response = await axios(requestConfig)
    return extractBlobResponse(response)
  } catch (error) {
    return handleAuthError<BlobResponse>(error, requestConfig, extractBlobResponse)
  }
}

// Standard API requests
export const getRequest = <T>(options: RequestOptions): Promise<T> => requestHandler<T>("GET", options)
export const postRequest = <T>(options: RequestOptions): Promise<T> => requestHandler<T>("POST", options)
export const putRequest = <T>(options: RequestOptions): Promise<T> => requestHandler<T>("PUT", options)
export const patchRequest = <T>(options: RequestOptions): Promise<T> => requestHandler<T>("PATCH", options)
export const deleteRequest = <T>(options: RequestOptions): Promise<T> => requestHandler<T>("DELETE", options)

// File API requests
export const fileRequest = <T>(options: FileRequestOptions): Promise<T> => fileRequestHandler<T>("POST", options)
export const putFileRequest = <T>(options: FileRequestOptions): Promise<T> => fileRequestHandler<T>("PUT", options)
export const patchFileRequest = <T>(options: FileRequestOptions): Promise<T> => fileRequestHandler<T>("PATCH", options)

// Blob API requests
export const getBlobRequest = (options: BlobRequestOptions): Promise<BlobResponse> => blobRequestHandler("GET", options)
